import { Suspense, lazy, useEffect, Component, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './stores/authStore';
import { useThemeStore, applyTheme } from './stores/themeStore';
import api from './lib/api';

export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, fontFamily: 'Inter, system-ui, sans-serif', background: 'var(--bg, #F4F1EC)', color: 'var(--danger, #E25555)', minHeight: '100dvh' }}>
          <h2 className="font-fraunces" style={{ color: 'var(--gold, #E89B30)', marginBottom: 16, fontSize: 22 }}>AXIS — Something went wrong</h2>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: 'var(--text, #1A1B22)' }}>{(this.state.error as Error).message}</pre>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, color: 'var(--muted, #5C6273)', marginTop: 12 }}>{(this.state.error as Error).stack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

import Auth from './pages/Auth';
import Onboard from './pages/Onboard';
import Today from './pages/Today';

const Finance = lazy(() => import('./pages/Finance'));
const Delegate = lazy(() => import('./pages/Delegate'));
const Tasks = lazy(() => import('./pages/Tasks'));
const Command = lazy(() => import('./pages/Command'));
const Advisor = lazy(() => import('./pages/Advisor'));
const AdvisorChat = lazy(() => import('./pages/AdvisorChat'));
const Settings = lazy(() => import('./pages/Settings'));
const More = lazy(() => import('./pages/More'));
const Meeting = lazy(() => import('./pages/Meeting'));
const Projects = lazy(() => import('./pages/Projects'));
const Ventures = lazy(() => import('./pages/Ventures'));
const AdvisoryDesk = lazy(() => import('./pages/AdvisoryDesk'));
const Personal = lazy(() => import('./pages/Personal'));
const Reminders = lazy(() => import('./pages/Reminders'));
const VoiceHistory = lazy(() => import('./pages/VoiceHistory'));
const Relations = lazy(() => import('./pages/Relations'));
const AxisChat = lazy(() => import('./pages/AxisChat'));
const Fitness = lazy(() => import('./pages/Fitness'));
const Memory = lazy(() => import('./pages/Memory'));
const RecurringJobs = lazy(() => import('./pages/RecurringJobs'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'var(--bg)',
  }}>
    <div className="lumen-orb" style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--gold)' }} />
    <div className="lumen-orb" style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--gold)', animationDelay: '0.15s', marginLeft: 8 }} />
    <div className="lumen-orb" style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--gold)', animationDelay: '0.3s', marginLeft: 8 }} />
  </div>
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuthStore();

  if (loading) {
    return <PageLoader />;
  }

  if (!session) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

function OnboardRoute({ children }: { children: React.ReactNode }) {
  const { user, session, loading } = useAuthStore();

  if (loading) {
    return <PageLoader />;
  }

  if (!session) {
    return <Navigate to="/auth" replace />;
  }

  const alreadyOnboarded = localStorage.getItem('axis_onboarded');
  if (
    alreadyOnboarded === 'true' ||
    user?.onboarding_complete ||
    user?.onboarding_completed
  ) {
    return <Navigate to="/today" replace />;
  }

  return <>{children}</>;
}

function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, session, loading } = useAuthStore();

  if (loading) {
    return <PageLoader />;
  }

  if (session && user) {
    const alreadyOnboarded = localStorage.getItem('axis_onboarded');
    if (
      user.onboarding_complete ||
      (user as any).onboarding_completed ||
      alreadyOnboarded === 'true'
    ) {
      return <Navigate to="/today" replace />;
    }
    return <Navigate to="/onboard" replace />;
  }

  return <>{children}</>;
}

function ThemeInit() {
  const { preference } = useThemeStore();
  useEffect(() => {
    applyTheme('light');
  }, [preference]);
  return null;
}

function AppInit() {
  const { session, setUser, setSession, setLoading, logout } = useAuthStore();

  // Sync Zustand when api.ts silently refreshes the access token
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.session) setSession(detail.session);
      if (detail?.user) setUser(detail.user);
    };
    window.addEventListener('axis:session-refreshed', handler);
    return () => window.removeEventListener('axis:session-refreshed', handler);
  }, [setSession, setUser]);

  useEffect(() => {
    const validateSession = async () => {
      if (!session?.access_token) {
        setLoading(false);
        return;
      }

      try {
        const { data } = await api.get('/auth/me');
        setUser(data.user);
        if (data.user?.onboarding_complete || data.user?.onboarding_completed) {
          localStorage.setItem('axis_onboarded', 'true');
        }
      } catch (err: any) {
        const status = err?.response?.status;
        // Try refresh once on 401
        if (status === 401 && session?.refresh_token) {
          try {
            const { data } = await api.post('/auth/refresh', { refresh_token: session.refresh_token });
            setSession(data.session);
            setUser(data.user);
            if (data.user?.onboarding_complete || data.user?.onboarding_completed) {
              localStorage.setItem('axis_onboarded', 'true');
            }
            return;
          } catch (refreshErr: any) {
            // Only log out if refresh itself definitively rejected the token (401/403).
            // Network errors, 5xx, timeouts → keep the session; user can retry later.
            const rStatus = refreshErr?.response?.status;
            if (rStatus === 401 || rStatus === 403) {
              logout();
            }
            return;
          }
        }
        // Only log out on definitive auth failures. Network errors, 5xx, timeouts —
        // keep the session and let user keep working with cached data.
        if (status === 401 || status === 403) {
          logout();
        }
      } finally {
        setLoading(false);
      }
    };

    validateSession();
  }, []);

  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeInit />
        <AppInit />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/auth" element={<AuthRoute><Auth /></AuthRoute>} />
            <Route path="/onboard" element={<OnboardRoute><Onboard /></OnboardRoute>} />
            <Route path="/today" element={<ProtectedRoute><Today /></ProtectedRoute>} />
            <Route path="/finance" element={<ProtectedRoute><Finance /></ProtectedRoute>} />
            <Route path="/command" element={<ProtectedRoute><Command /></ProtectedRoute>} />
            <Route path="/tasks" element={<Navigate to="/command" replace />} />
            <Route path="/delegate" element={<Navigate to="/command" replace />} />
            <Route path="/reminders" element={<Navigate to="/command" replace />} />
            <Route path="/advisor" element={<ProtectedRoute><Advisor /></ProtectedRoute>} />
            <Route path="/advisor/:advisorType" element={<ProtectedRoute><AdvisorChat /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/more" element={<ProtectedRoute><More /></ProtectedRoute>} />
            <Route path="/meeting" element={<ProtectedRoute><Meeting /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
            <Route path="/ventures" element={<ProtectedRoute><Ventures /></ProtectedRoute>} />
            <Route path="/ventures/advisory" element={<ProtectedRoute><AdvisoryDesk /></ProtectedRoute>} />
            <Route path="/personal" element={<ProtectedRoute><Personal /></ProtectedRoute>} />
            <Route path="/voice-history" element={<ProtectedRoute><VoiceHistory /></ProtectedRoute>} />
            <Route path="/relations" element={<ProtectedRoute><Relations /></ProtectedRoute>} />
            <Route path="/fitness" element={<ProtectedRoute><Fitness /></ProtectedRoute>} />
            <Route path="/memory" element={<ProtectedRoute><Memory /></ProtectedRoute>} />
            <Route path="/recurring-jobs" element={<ProtectedRoute><RecurringJobs /></ProtectedRoute>} />
            <Route path="/chat" element={<ProtectedRoute><AxisChat /></ProtectedRoute>} />
            <Route path="/" element={<Navigate to="/today" replace />} />
            <Route path="*" element={<Navigate to="/auth" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
